import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

export default function MemberMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const { qboConnectionId } = useParams();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);

  const [{ members, vendors, isReady }, setState] = useState({ members: null, isReady: false });

  const errorMappings = useMemo(() => {
    if (!members) return {};
    return members
      .filter((m) => m.qboVendorId && members.find((o) => o.qboVendorId === m.qboVendorId && o.id !== m.id))
      .reduce(
        (acc, m) => ({
          ...acc,
          [m.id]: true,
        }),
        {},
      );
  }, [members]);

  const usedQboVendorIds = useMemo(
    () => members?.filter((m) => m.qboVendorId).map((m) => m.qboVendorId) || [],
    [members],
  );

  const hasErrors = useMemo(() => {
    return Object.values(errorMappings).some((v) => v);
  }, [errorMappings]);

  useEffect(() => {
    (async () => {
      const { data: members } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getMemberMappings();
      const { data: vendors } = await api.www.workspaces(workspace.id).qbo(qboConnectionId).getVendors();

      if (!isMounted.current) return;
      setState({ members, vendors, isReady: true });
    })();
  }, [api, workspace.id, isMounted, qboConnectionId]);

  const updateMemberMapping = ({ memberId, qboVendorId }) => {
    setDirty(true);

    setState((state) => ({
      ...state,
      members: state.members.map((m) => (m.id === memberId ? { ...m, qboVendorId } : m)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Member Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();

          try {
            const mappings = members.map((member) => ({ memberId: member.id, qboVendorId: member.qboVendorId }));
            await api.www.workspaces(workspace.id).qbo(qboConnectionId).updateMemberMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="25%">Member</Table.Column>
                <Table.Column width="25%">Email</Table.Column>
                <Table.Column>QuickBooks Vendor</Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {members.map((member) => {
                  const handleVendorChange = (event) => {
                    const qboVendorId = event.target.value || null;

                    updateMemberMapping({
                      memberId: member.id,
                      qboVendorId,
                    });
                  };

                  const availableVendors = vendors.filter(
                    (vendor) => !usedQboVendorIds.includes(vendor.id) || vendor.id === member.qboVendorId,
                  );

                  const error = errorMappings[member.id]
                    ? { message: 'Vendor already mapped to another member' }
                    : null;

                  return (
                    <Table.BoxRow key={member.id}>
                      <Table.Cell>
                        {member.name}
                        {!member.isActive && (
                          <Tooltip message="Inactive member">
                            <Icon icon="ban" color={colors.grey40} style={{ marginLeft: '.5rem' }} />
                          </Tooltip>
                        )}
                      </Table.Cell>
                      <Table.Cell>{member.email}</Table.Cell>
                      <Table.Cell>
                        <VendorSelect
                          vendors={availableVendors}
                          value={member.qboVendorId}
                          onChange={handleVendorChange}
                          error={error}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave} disabled={hasErrors}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function VendorSelect({ vendors, value, error, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const options = useMemo(() => {
    if (!filterValue) {
      return vendors;
    }

    const fuse = new Fuse(vendors, { keys: ['displayName'], threshold: 0.1 });
    const results = fuse.search(filterValue);
    return results.map((result) => result.item);
  }, [vendors, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const vendor = _.find(vendors, { id: value });
    if (!vendor)
      return {
        id: value,
        name: (
          <Tooltip message="Vendor not found in QuickBooks.">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Vendor ID: {value}
          </Tooltip>
        ),
      };
    if (error)
      return {
        id: value,
        name: (
          <Tooltip message={error.message}>
            <Icon icon="exclamation-circle" color={colors.error} spaceRight />
            {vendor.displayName}
          </Tooltip>
        ),
      };
    return { id: vendor.id, name: vendor.displayName };
  }, [vendors, value, error]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {options.map((option) => (
        <option key={option.id} value={option.id}>
          {option.displayName}
        </option>
      ))}
    </SingleSelect>
  );
}
